import {
  Button,
  Dialog,
  DialogActions,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField
} from "@mui/material";
import { useEffect, useState } from "react";
import { FiAlertCircle, FiEdit, FiTrash2 } from "react-icons/fi";
import CreateButton from "../../components/CreateButton";
import InfoBanner from "../../components/InfoBanner";
import { useAuth } from "../../hooks/useAuth";
import api from "../../services/api";
import {
  ActionButton,
  ActionsCell,
  Container,
  SchedulingSearchCard,
  StatusCell,
  StatusMyNotify,
  Table,
  TableContainer,
  TextFeldClientSearch,
  Title
} from "./pending.styled";

import { PendingInterface } from "../../common/interfaces";
import CustomTooltip from "../../components/CustomTooltip";
import {
  SchedulingStatusLabel,
  TypeSchedulingStatus
} from "../../components/enum/schedulingStatusEnum";
import HelpVideoButton from "../../components/HelpVideoButton";
import PendingCreateModal from "../../components/modal/PendingCreateModal";
import PaginationControls from "../../components/PaginationControls";
import StatusSwitch from "../../components/switch/StatusSwitch";

import { makeToasterErrorHandler } from "../../common/http/makeToasterErrorHandler";
import PageToaster from "../../components/toaster/PageToaster";
import { usePageToast } from "../../hooks/usePageToast";

export const PendingPage = () => {
  const [search, setSearch] = useState("");

  const [pendings, setPendings] = useState<PendingInterface[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Controle do diálogo de confirmação de exclusão
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pendingToDelete, setPendingToDelete] = useState<number | null>(null);

  // **Controle do modal de criação/edição**
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [selectedPendings, setSelectedPendings] =
    useState<PendingInterface | null>(null);

  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");

  const [statusValue, setStatusValue] = useState<TypeSchedulingStatus>(
    TypeSchedulingStatus.NONE
  );

  const containerId = "toaster-pending-create-modal";
  const { autoSuccess, autoError, autoInfo, autoWarn } = usePageToast(
    containerId,
    true
  );

  const handleHttpError = makeToasterErrorHandler(autoError);

  const { isAdmin, isUser } = useAuth();

  // Busca agendamentos paginados
  const fetchSchedules = async (currentPage: number) => {
    try {
      const response = await api.get(`/pending`, {
        params: {
          page: currentPage,
          limit: 8,
          search: search.trim() !== "" ? search.trim() : undefined,
          initial_date: startDate || undefined,
          final_date: endDate || undefined,
          pending_status:
            statusValue !== TypeSchedulingStatus.NONE ? statusValue : undefined
        }
      });

      setPendings(response.data.items);
      setTotalPages(response.data.meta.totalPages);
    } catch (error) {
      handleHttpError(error);
      console.error("Erro ao buscar pendência:", error);
    }
  };

  useEffect(() => {
    fetchSchedules(page);
  }, [page, search]);

  // Abre diálogo de confirmação antes de excluir agendamento
  const openDeleteDialog = (pending: PendingInterface) => {
    setPendingToDelete(pending.pending_id);
    setDialogOpen(true);
  };

  const handleCancelDelete = () => {
    setDialogOpen(false);
    setPendingToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (pendingToDelete !== null) {
      try {
        await api.delete(`/pending/${pendingToDelete}`);
        autoSuccess(`Agendamento removido com sucesso!`);
        fetchSchedules(page);
      } catch (error) {
        console.log("error: ", error);
        handleHttpError(error);
        console.error("Erro ao deletar agendamento:", error);
      } finally {
        setDialogOpen(false);
        setPendingToDelete(null);
      }
    }
  };

  // ***********************
  // 1) Criar um handle que abre o modal em modo “novo agendamento”
  // ***********************
  const handleOpenCreateModal = () => {
    setSelectedPendings(null); // garante que o modal abra em branco
    setIsModalOpen(true);
  };

  // ***********************
  // 2) Criar um handle que abre o modal em modo “edição”, já carregando os dados
  // ***********************
  const handleOpenEditModal = (sched: PendingInterface) => {
    setSelectedPendings(sched);
    setIsModalOpen(true);
  };

  // ***********************
  // 3) Recarrega lista e fecha modal quando ele for fechado
  // ***********************
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedPendings(null);
    fetchSchedules(page);
  };

  const handleToggleStatus = async (pending: PendingInterface) => {
    try {
      let current_status = !pending.notify_active;
      await api.patch(`/pending/${pending.pending_id}`, {});

      if (current_status) {
        autoSuccess("Notificação ativada com sucesso!");
      } else {
        autoWarn("Notificação desativada com sucesso!");
      }

      fetchSchedules(page); // Atualiza a lista após o toggle
    } catch (error) {
      console.error("Erro ao atualizar status do cliente:", error);
      handleHttpError(error);
    }
  };

  useEffect(() => {
    fetchSchedules(page);
  }, [page, search, startDate, endDate, statusValue]);

  return (
    <Container>
      <PageToaster containerId={containerId} />
      <Title>
        Lista de Pendências
        <CreateButton
          onClick={handleOpenCreateModal}
          style={{ marginLeft: "14px" }}
        >
          <FiAlertCircle size={18} /> Criar Pendência
        </CreateButton>
        <HelpVideoButton
          youtubeUrl="https://youtu.be/rrHKuYnAAHs"
          modalTitle="Gerenciar Pendencias"
          sx={{
            position: "absolute",
            top: 0,
            right: 8,
            zIndex: 2
          }}
        />
      </Title>

      <InfoBanner icon={<FiAlertCircle size={26} />} title="Pendências">
        Anote lembretes e afazeres da empresa que não têm hora marcada nem
        cliente vinculado — diferente de Agendamentos, que são compromissos
        com um cliente específico.
      </InfoBanner>

      <SchedulingSearchCard>
        <TextField
          label="Data de Início"
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          inputProps={{
            max: new Date().toLocaleDateString("en-CA") // impede datas futuras no fuso local
          }}
        />

        <TextField
          disabled={!startDate}
          label="Data Final"
          type="date"
          value={startDate ? endDate : ""}
          onChange={(e) => setEndDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          inputProps={{
            min: startDate || undefined, // impede menor que startDate
            max: new Date().toLocaleDateString("en-CA") // impede datas futuras
          }}
        />

        <TextFeldClientSearch
          id="scheduling_search"
          label="Pendência"
          variant="outlined"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1); // reseta para primeira página ao buscar
          }}
          style={{ marginBottom: "16px", width: "300px" }}
        />

        <FormControl style={{ minWidth: 200 }}>
          <InputLabel id="label-status">Status</InputLabel>
          <Select
            labelId="label-status"
            value={statusValue}
            label="Status"
            onChange={(e) =>
              setStatusValue(e.target.value as TypeSchedulingStatus)
            }
          >
            {(Object.values(TypeSchedulingStatus) as TypeSchedulingStatus[])
              .filter((code) => code !== TypeSchedulingStatus.NONE)
              .map((statusCode) => (
                <MenuItem key={statusCode} value={statusCode}>
                  {SchedulingStatusLabel[statusCode]}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
      </SchedulingSearchCard>

      <TableContainer>
        <Table>
          <thead>
            <tr>
              <th>Data / Hora</th>
              <th>Titulo</th>
              <th>Descrição</th>
              <th>Status</th>
              {isAdmin && <th>Empresa</th>}
              <th>Usuário</th>
              <th>Notificação</th>
              {!isUser && <th>Ações</th>}
            </tr>
          </thead>
          <tbody>
            {pendings.map((current_pending) => {
              // Formata data e hora para pt-BR
              const dateObj = new Date(current_pending.pending_date);
              const formattedDate = dateObj.toLocaleDateString("pt-BR", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric"
              });
              const formattedTime = dateObj.toLocaleTimeString("pt-BR", {
                hour: "2-digit",
                minute: "2-digit"
              });

              return (
                <tr key={current_pending.pending_id}>
                  <td>{`${formattedDate} ${formattedTime}`}</td>
                  <td>{current_pending.pending_title}</td>
                  <td>{current_pending.pending_description}</td>
                  {/* <td>{sched.scheduling_status}</td> */}
                  <StatusCell status={current_pending.pending_status}>
                    {SchedulingStatusLabel[current_pending.pending_status]}
                  </StatusCell>

                  {isAdmin && <td>{current_pending.company.company_name}</td>}
                  <td>{current_pending.user.user_name}</td>
                  {isAdmin ? (
                    <td>
                      {" "}
                      <StatusSwitch
                        checked={current_pending.notify_active}
                        onToggle={() => handleToggleStatus(current_pending)}
                      />
                    </td>
                  ) : current_pending.notify_active ? (
                    <StatusMyNotify status={current_pending.notify_active}>
                      Ativo
                    </StatusMyNotify>
                  ) : (
                    <StatusMyNotify status={current_pending.notify_active}>
                      Inativo
                    </StatusMyNotify>
                  )}
                  {!isUser && (
                    <ActionsCell>
                      <CustomTooltip title="Editar Pendência">
                        <ActionButton
                          onClick={() => handleOpenEditModal(current_pending)}
                        >
                          <FiEdit size={20} color="#4caf50" />
                        </ActionButton>
                      </CustomTooltip>

                      <CustomTooltip title="Excluir Pendência">
                        <ActionButton
                          onClick={() => openDeleteDialog(current_pending)}
                        >
                          <FiTrash2 size={20} color="#f44336" />
                        </ActionButton>
                      </CustomTooltip>
                    </ActionsCell>
                  )}
                </tr>
              );
            })}
          </tbody>
        </Table>
      </TableContainer>

      <PaginationControls
        page={page}
        totalPages={totalPages}
        setPage={setPage}
      />

      <Dialog open={dialogOpen} onClose={handleCancelDelete}>
        <DialogTitle>Confirmar Exclusão</DialogTitle>

        <DialogActions>
          <Button onClick={handleCancelDelete} color="secondary">
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="primary"
            variant="contained"
          >
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

      <PendingCreateModal
        open={isModalOpen}
        onClose={handleCloseModal}
        pending={selectedPendings}
      />
    </Container>
  );
};
